import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { UIProvider } from './contexts/UIContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import DealerDashboard from './pages/DealerDashboard';
import DealerList from './pages/DealerList';
import DealerForm from './pages/DealerForm';
import EmbedCode from './pages/EmbedCode';
import DealerUsers from './pages/DealerUsers';
import AdminUsers from './pages/AdminUsers';
import Calendar from './pages/Calendar';
import LocationHours from './pages/LocationHours';
import LocationServices from './pages/LocationServices';
import DealerLocations from './pages/DealerLocations';
import LocationBays from './pages/LocationBays';
import DealerServices from './pages/DealerServices';
import LocationCalendar from './pages/LocationCalendar';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({ children, adminOnly = false }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: '#f9fafb'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #e5e7eb',
          borderTop: '4px solid #ed2024',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If this is an admin-only route but user is a dealer, redirect to dealer dashboard
  if (adminOnly && user.userType === 'dealer') {
    return <Navigate to="/dealer-dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Dashboard Router Component - redirects based on user type
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();
  
  if (user?.userType === 'dealer') {
    return <Navigate to="/dealer-dashboard" replace />;
  }
  
  // Redirect admin users to Dealer Management page
  return <Navigate to="/dealers" replace />;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      <Route path="/dealer-dashboard" element={
        <ProtectedRoute>
          <DealerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Calendar />
        </ProtectedRoute>
      } />
      <Route path="/calendar/:id" element={
        <ProtectedRoute adminOnly>
          <Calendar />
        </ProtectedRoute>
      } />
      <Route path="/locations" element={
        <ProtectedRoute>
          <DealerLocations />
        </ProtectedRoute>
      } />
      <Route path="/dealers" element={
        <ProtectedRoute adminOnly>
          <DealerList />
        </ProtectedRoute>
      } />
      <Route path="/dealers/new" element={
        <ProtectedRoute adminOnly>
          <DealerForm />
        </ProtectedRoute>
      } />
      <Route path="/dealers/:id/edit" element={
        <ProtectedRoute adminOnly>
          <DealerForm />
        </ProtectedRoute>
      } />
      <Route path="/dealers/:id/embed" element={
        <ProtectedRoute>
          <EmbedCode />
        </ProtectedRoute>
      } />
      <Route path="/dealers/:id/users" element={
        <ProtectedRoute>
          <DealerUsers />
        </ProtectedRoute>
      } />
      <Route path="/team-users" element={
        <ProtectedRoute>
          <DealerUsers />
        </ProtectedRoute>
      } />
      <Route path="/dealers/:id/calendar" element={
        <ProtectedRoute adminOnly>
          <Calendar />
        </ProtectedRoute>
      } />
      <Route path="/locations/:locationId/hours" element={
        <ProtectedRoute>
          <LocationHours />
        </ProtectedRoute>
      } />
      <Route path="/locations/:locationId/services" element={
        <ProtectedRoute>
          <LocationServices />
        </ProtectedRoute>
      } />
      <Route path="/locations/:locationId/bays" element={
        <ProtectedRoute>
          <LocationBays />
        </ProtectedRoute>
      } />
      <Route path="/locations/:locationId/calendar" element={
        <ProtectedRoute>
          <LocationCalendar />
        </ProtectedRoute>
      } />
      <Route path="/dealers/:dealerId/services" element={
        <ProtectedRoute adminOnly>
          <DealerServices />
        </ProtectedRoute>
      } />
      <Route path="/services" element={
        <ProtectedRoute>
          <DealerServices />
        </ProtectedRoute>
      } />
      {/* Placeholder routes for future features */}
      <Route path="/embed-codes" element={
        <ProtectedRoute adminOnly>
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <h2>Embed Codes</h2>
            <p>This feature is coming soon. Use the individual dealer embed codes for now.</p>
          </div>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute adminOnly>
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <h2>Analytics</h2>
            <p>Usage analytics and reporting features coming soon.</p>
          </div>
        </ProtectedRoute>
      } />
      <Route path="/admin-users" element={
        <ProtectedRoute adminOnly>
          <AdminUsers />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute adminOnly>
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <h2>System Settings</h2>
            <p>System configuration features coming soon.</p>
          </div>
        </ProtectedRoute>
      } />
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <UIProvider>
        <Router>
          <div className="App">
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  style: {
                    background: '#10b981',
                  },
                },
                error: {
                  style: {
                    background: '#ef4444',
                  },
                },
              }}
            />
          </div>
        </Router>
      </UIProvider>
    </AuthProvider>
  );
};

export default App;
